import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Box,
    Button,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Paper,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import Popup from "../../components/Popup";
import { StyledTableCell, StyledTableRow } from "../../components/styles";
import {
    createLessonPlan,
    deleteLessonPlan,
    getMyLessonPlans,
    updateLessonPlan,
} from "../../redux/teacherRelated/teacherToolsHandle";
import { underToolControl } from "../../redux/teacherRelated/teacherToolsSlice";
import { useTeacherTeaching } from "../../hooks/useTeacherTeaching";

function todayInputValue() {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}

function formatDateDDMMYYYY(value) {
    if (!value) return "—";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
}

function homeworkPreview(text) {
    const t = String(text || "").trim();
    if (!t) return "None";
    return t.length > 40 ? `${t.slice(0, 40)}…` : t;
}

function toDateInputValue(value) {
    if (!value) return todayInputValue();
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return todayInputValue();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}

const emptyForm = () => ({
    lessonDate: todayInputValue(),
    duration: 40,
    topic: "",
    subtopic: "",
    objectives: "",
    introduction: "",
    development: "",
    conclusion: "",
    resources: "",
    homework: "",
    reflection: "",
});

const TeacherLessonPlans = () => {
    const dispatch = useDispatch();
    const { currentUser } = useSelector((s) => s.user);
    const { lessonPlansList, loading, statestatus, response, error } = useSelector((s) => s.teacherTools);

    const teacherId = currentUser?._id;
    const schoolId = currentUser?.school?._id || currentUser?.schoolId;
    const { activeSubjectId: subjectId, activeClassId: classId, subjectName, className } = useTeacherTeaching();

    const [showPopup, setShowPopup] = useState(false);
    const [message, setMessage] = useState("");

    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(emptyForm);

    const lessons = useMemo(
        () => (Array.isArray(lessonPlansList) ? lessonPlansList : []),
        [lessonPlansList]
    );

    const reloadLessons = useCallback(
        (filters = {}) => {
            if (!teacherId) return;
            const params = {};
            if (filters.from) params.from = filters.from;
            if (filters.to) params.to = filters.to;
            dispatch(getMyLessonPlans(teacherId, params));
        },
        [dispatch, teacherId]
    );

    useEffect(() => {
        reloadLessons();
    }, [reloadLessons]);

    useEffect(() => {
        if (statestatus === "added") {
            setMessage("Completed successfully!");
            setShowPopup(true);
            reloadLessons({
                from: dateFrom || undefined,
                to: dateTo || undefined,
            });
            setDialogOpen(false);
            setEditingId(null);
            setForm(emptyForm());
            dispatch(underToolControl());
        }
    }, [statestatus, dispatch, reloadLessons, dateFrom, dateTo]);

    useEffect(() => {
        const msg = response || error;
        if (msg && statestatus !== "added") {
            setMessage(typeof msg === "string" ? msg : "Request failed");
            setShowPopup(true);
            dispatch(underToolControl());
        }
    }, [response, error, statestatus, dispatch]);

    const setField = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const openCreate = () => {
        setEditingId(null);
        setForm(emptyForm());
        setDialogOpen(true);
    };

    const openEdit = (row) => {
        setEditingId(row.id || row._id);
        setForm({
            lessonDate: toDateInputValue(row.lessonDate),
            duration: Number(row.duration) || 40,
            topic: row.topic || "",
            subtopic: row.subtopic || "",
            objectives: row.objectives || "",
            introduction: row.introduction || "",
            development: row.development || "",
            conclusion: row.conclusion || "",
            resources: row.resources || "",
            homework: row.homework || "",
            reflection: row.reflection || "",
        });
        setDialogOpen(true);
    };

    const handleApplyFilter = () => {
        reloadLessons({ from: dateFrom || undefined, to: dateTo || undefined });
    };

    const buildPayload = () => ({
        teacherId,
        schoolId,
        subjectId,
        classId,
        lessonDate: form.lessonDate,
        topic: String(form.topic).trim(),
        subtopic: String(form.subtopic || "").trim(),
        objectives: String(form.objectives || "").trim(),
        introduction: String(form.introduction || "").trim(),
        development: String(form.development || "").trim(),
        conclusion: String(form.conclusion || "").trim(),
        resources: String(form.resources || "").trim(),
        homework: String(form.homework || "").trim(),
        reflection: String(form.reflection || "").trim(),
        duration: Number(form.duration) > 0 ? Number(form.duration) : 40,
    });

    const handleSubmit = () => {
        if (!teacherId || !schoolId || !subjectId || !classId) {
            setMessage("Missing teacher, school, subject or class information");
            setShowPopup(true);
            return;
        }
        if (!String(form.topic || "").trim()) {
            setMessage("Topic is required");
            setShowPopup(true);
            return;
        }
        if (!form.lessonDate) {
            setMessage("Lesson date is required");
            setShowPopup(true);
            return;
        }

        const payload = buildPayload();
        if (editingId) {
            const { teacherId: _t, schoolId: _s, subjectId: _sub, classId: _c, ...updateBody } = payload;
            dispatch(updateLessonPlan(editingId, updateBody));
        } else {
            dispatch(createLessonPlan(payload));
        }
    };

    const handleDelete = (id) => {
        if (window.confirm("Delete this lesson plan?")) {
            dispatch(deleteLessonPlan(id));
        }
    };

    return (
        <Container maxWidth="lg" sx={{ py: { xs: 2, md: 3 } }}>
            <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "stretch", sm: "flex-start" }}
                spacing={2}
                sx={{ mb: 2 }}
            >
                <Box>
                    <Typography variant="h5" fontWeight={700}>
                        Lesson Plans
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 560 }}>
                        Prepare and record daily lesson notes for each class period.
                    </Typography>
                </Box>
                <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={{ flexShrink: 0 }}>
                    Log Lesson Plan
                </Button>
            </Stack>

            <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "flex-end" }} flexWrap="wrap">
                    <TextField
                        size="small"
                        type="date"
                        label="Date from"
                        value={dateFrom}
                        onChange={(e) => setDateFrom(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ minWidth: 160 }}
                    />
                    <TextField
                        size="small"
                        type="date"
                        label="Date to"
                        value={dateTo}
                        onChange={(e) => setDateTo(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ minWidth: 160 }}
                    />
                    <Button variant="outlined" onClick={handleApplyFilter}>
                        Apply
                    </Button>
                </Stack>
            </Paper>

            <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
                <TableContainer sx={{ overflowX: "auto" }}>
                    <Table size="small">
                        <TableHead>
                            <StyledTableRow>
                                <StyledTableCell>Date</StyledTableCell>
                                <StyledTableCell>Topic</StyledTableCell>
                                <StyledTableCell>Subject</StyledTableCell>
                                <StyledTableCell>Class</StyledTableCell>
                                <StyledTableCell>Duration</StyledTableCell>
                                <StyledTableCell>Homework</StyledTableCell>
                                <StyledTableCell align="right">Actions</StyledTableCell>
                            </StyledTableRow>
                        </TableHead>
                        <TableBody>
                            {loading && lessons.length === 0 ? (
                                <StyledTableRow>
                                    <StyledTableCell colSpan={7}>
                                        <Skeleton height={48} />
                                    </StyledTableCell>
                                </StyledTableRow>
                            ) : lessons.length === 0 ? (
                                <StyledTableRow>
                                    <StyledTableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            No lesson plans found for this period.
                                        </Typography>
                                    </StyledTableCell>
                                </StyledTableRow>
                            ) : (
                                lessons.map((row) => {
                                    const id = row.id || row._id;
                                    return (
                                        <StyledTableRow key={id} hover>
                                            <StyledTableCell>{formatDateDDMMYYYY(row.lessonDate)}</StyledTableCell>
                                            <StyledTableCell>{row.topic}</StyledTableCell>
                                            <StyledTableCell>{row.subject?.subName || subjectName}</StyledTableCell>
                                            <StyledTableCell>{row.class?.sclassName || className}</StyledTableCell>
                                            <StyledTableCell>{Number(row.duration) || 40} mins</StyledTableCell>
                                            <StyledTableCell>{homeworkPreview(row.homework)}</StyledTableCell>
                                            <StyledTableCell align="right">
                                                <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                    <Tooltip title="View / edit">
                                                        <IconButton size="small" color="primary" onClick={() => openEdit(row)}>
                                                            <EditOutlinedIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Delete">
                                                        <IconButton size="small" color="error" onClick={() => handleDelete(id)}>
                                                            <DeleteOutlineIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Stack>
                                            </StyledTableCell>
                                        </StyledTableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>{editingId ? "Edit Lesson Plan" : "Log Lesson Plan"}</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        <strong>Subject:</strong> {subjectName} · <strong>Class:</strong> {className}
                    </Typography>

                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                required
                                size="small"
                                type="date"
                                label="Lesson date"
                                value={form.lessonDate}
                                onChange={(e) => setField("lessonDate", e.target.value)}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label="Duration"
                                value={form.duration}
                                onChange={(e) => setField("duration", e.target.value)}
                                helperText="minutes"
                                inputProps={{ min: 1 }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                required
                                size="small"
                                label="Topic"
                                value={form.topic}
                                onChange={(e) => setField("topic", e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Subtopic"
                                value={form.subtopic}
                                onChange={(e) => setField("subtopic", e.target.value)}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                minRows={3}
                                label="Learning objectives"
                                placeholder="By the end of this lesson, students should be able to..."
                                value={form.objectives}
                                onChange={(e) => setField("objectives", e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                minRows={3}
                                label="Introduction / lesson opener"
                                placeholder="How you will start the lesson — prior knowledge check, hook activity"
                                value={form.introduction}
                                onChange={(e) => setField("introduction", e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                minRows={3}
                                label="Lesson development (main activity)"
                                placeholder="Step-by-step teaching activity, explanations, demonstrations"
                                value={form.development}
                                onChange={(e) => setField("development", e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                minRows={3}
                                label="Conclusion / summary"
                                placeholder="How you will end the lesson — recap, takeaway, closing activity"
                                value={form.conclusion}
                                onChange={(e) => setField("conclusion", e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                minRows={3}
                                label="Resources required"
                                placeholder="Chalk, textbook (page X), charts, calculators..."
                                value={form.resources}
                                onChange={(e) => setField("resources", e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                minRows={3}
                                label="Homework set"
                                placeholder="Homework assigned at end of lesson (or leave blank)"
                                value={form.homework}
                                onChange={(e) => setField("homework", e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                minRows={3}
                                label="Teacher reflection"
                                placeholder="What went well? What would you change next time?"
                                value={form.reflection}
                                onChange={(e) => setField("reflection", e.target.value)}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSubmit} disabled={loading}>
                        {editingId ? "Update" : "Save"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Popup message={message} showPopup={showPopup} setShowPopup={setShowPopup} />
        </Container>
    );
};

export default TeacherLessonPlans;
